import { useQuery, useMutation, type UseQueryResult } from '@tanstack/react-query';
import type { PaginatedResponse } from '../../types/pagination.type';
import { vectorDbApi } from '../services/vectorDbApi';
import type {
  VectorDbSyncName,
  VectorDbSyncCheckResult,
  VectorDbSyncQueuedResult,
  VectorDbJobHistoryItem,
  VectorDbJobHistoryParams,
} from '../types/vector-db.type';

export const vectorDbKeys = {
  all: ['admin', 'vector-db'] as const,
  syncCheck: (name: VectorDbSyncName) => [...vectorDbKeys.all, 'sync-check', name] as const,
  jobHistory: (name: VectorDbSyncName, params: VectorDbJobHistoryParams) =>
    [...vectorDbKeys.all, 'job-history', name, params] as const,
};

export function useVectorDbSyncCheck(name: VectorDbSyncName): UseQueryResult<VectorDbSyncCheckResult, Error> {
  return useQuery({
    queryKey: vectorDbKeys.syncCheck(name),
    queryFn: () => vectorDbApi.checkVectorDbSync(name),
    staleTime: 0,
  });
}

/** One API call per page — same page/pageSize contract as useAdminProviders. */
export function useVectorDbJobHistory(
  name: VectorDbSyncName,
  params: VectorDbJobHistoryParams,
): UseQueryResult<PaginatedResponse<VectorDbJobHistoryItem>, Error> {
  return useQuery({
    queryKey: vectorDbKeys.jobHistory(name, params),
    queryFn: () => vectorDbApi.getVectorDbJobHistory(name, params),
    staleTime: 0,
    placeholderData: (previousData) => previousData, // keep old page visible while the next loads
  });
}

export function useSyncProvidersVectorDb() {
  return useMutation<VectorDbSyncQueuedResult, Error>({
    mutationFn: () => vectorDbApi.syncProvidersVectorDb(),
  });
}

export function useSyncResourcesVectorDb() {
  return useMutation<VectorDbSyncQueuedResult, Error>({
    mutationFn: () => vectorDbApi.syncResourcesVectorDb(),
  });
}
